(() => {
  if (customElements.get('product-modal')) return;
  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const reduced = () => document.body.dataset.acMotion === 'reduced' || matchMedia('(prefers-reduced-motion: reduce)').matches;
  const fullImage = async (opener) => {
    const image = new Image();
    image.decoding = 'async';
    image.src = opener.dataset.acZoomSrc;
    await image.decode();
    return image;
  };

  class ProductImageViewer extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.events = new AbortController();
      const on = (target, type, listener, options = {}) => target.addEventListener(type, listener, { ...options, signal: this.events.signal });
      this.stage = this.querySelector('[data-ac-zoom-stage]');
      this.image = this.querySelector('[data-ac-zoom-image]');
      this.resetButton = this.querySelector('[data-ac-zoom-reset]');
      this.status = this.querySelector('.ac-product-zoom__status');
      this.loading = this.querySelector('[data-ac-zoom-loading]');
      this.retry = this.querySelector('[data-ac-zoom-retry]');
      this.pointers = new Map();
      this.request ??= 0;
      on(this.querySelector('[data-ac-zoom-close]'), 'click', () => this.hide());
      on(this.resetButton, 'click', () => this.reset(true));
      on(this.retry, 'click', () => this.loadFull());
      on(this.stage, 'wheel', (event) => {
        event.preventDefault();
        const delta = event.deltaY * (event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.viewHeight : 1);
        this.zoom(this.scale * Math.exp(-delta * 0.002), this.point(event));
      }, { passive: false });
      on(this.stage, 'dblclick', (event) => {
        if (event.target !== this.image && this.backdrop) return;
        this.zoom(this.scale > 1 ? 1 : Math.min(this.maxScale, Math.max(2, 1 / this.fit)), this.point(event), true);
      });
      on(this.stage, 'pointerdown', (event) => {
        if (event.button !== 0 || this.closing || this.pointers.size >= 2) return;
        if (!this.pointers.size) { this.moved = false; this.backdrop = event.target === this.stage; }
        this.moveAnimation?.cancel();
        this.pointers.set(event.pointerId, this.point(event));
        if (this.scale > 1 || event.pointerType === 'touch') this.stage.setPointerCapture(event.pointerId);
        this.stage.classList.add('is-dragging');
      });
      on(this.stage, 'pointermove', (event) => this.move(event));
      const end = (event) => {
        this.pointers.delete(event.pointerId);
        if (this.stage.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
        if (!this.pointers.size) this.stage.classList.remove('is-dragging');
      };
      for (const type of ['pointerup', 'pointercancel', 'lostpointercapture']) on(this.stage, type, end);
      on(this.stage, 'click', (event) => {
        if (this.backdrop && !this.moved) this.hide();
      });
      on(this, 'keydown', (event) => {
        if (event.target.closest('button')) return;
        if (event.key === '+' || event.key === '=') this.zoom(this.scale * 1.25, { x: 0, y: 0 }, true);
        else if (event.key === '-') this.zoom(this.scale / 1.25, { x: 0, y: 0 }, true);
        else if (event.key === '0') this.reset(true);
        else if (this.scale > 1 && /^Arrow/.test(event.key)) {
          this.x += event.key === 'ArrowLeft' ? 80 : event.key === 'ArrowRight' ? -80 : 0;
          this.y += event.key === 'ArrowUp' ? 80 : event.key === 'ArrowDown' ? -80 : 0;
          this.render(true);
        } else return;
        event.preventDefault();
      });
      this.resizeObserver = new ResizeObserver(() => {
        if (this.hasAttribute('open') && !this.closing) this.measure();
      });
      this.resizeObserver.observe(this.stage);
      // Keep the viewer outside the product branch that the shared manager inerts.
      if (this.parentElement !== document.body) document.body.append(this);
    }

    disconnectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected) return;
        this.hide(true);
        this.events?.abort();
        this.resizeObserver?.disconnect();
        this.events = null;
      });
    }

    show(opener) {
      clearHover();
      this.openedBy = opener;
      this.closing = false;
      this.animation?.cancel();
      this.sourceWidth = Number(opener.dataset.acZoomWidth);
      this.sourceHeight = Number(opener.dataset.acZoomHeight);
      const preview = opener.querySelector('img');
      this.image.alt = preview?.alt || '';
      this.image.src = preview?.currentSrc || preview?.src || opener.dataset.acZoomSrc;
      this.setAttribute('open', '');
      this.scale = 1;
      this.x = this.y = 0;
      this.measure();
      window.AcLayerManager.acquire(this.id, { opener, getFocusRoots: () => [this], onEscape: () => this.hide() });
      if (document.documentElement.dataset.acInputMode === 'keyboard') this.stage.focus({ preventScroll: true });
      window.pauseAllMedia?.();
      this.animation = this.animate([{ opacity: 0 }, { opacity: 1 }], { duration: reduced() ? 0 : 220, easing: 'ease-out' });
      this.loadFull();
    }

    async loadFull() {
      const request = ++this.request;
      this.status.hidden = false;
      this.loading.hidden = false;
      this.retry.hidden = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const full = await fullImage(this.openedBy);
        if (request !== this.request || this.closing || !this.hasAttribute('open')) return;
        this.sourceWidth = full.naturalWidth;
        this.sourceHeight = full.naturalHeight;
        this.image.src = full.src;
        this.measure();
        this.status.hidden = true;
        this.image.animate([{ opacity: 0.7 }, { opacity: 1 }], { duration: reduced() ? 0 : 140 });
      } catch {
        if (request !== this.request) return;
        this.loading.hidden = true;
        this.retry.hidden = false;
      } finally {
        if (request === this.request) this.removeAttribute('aria-busy');
      }
    }

    measure() {
      const style = getComputedStyle(this.stage);
      this.viewWidth = this.stage.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      this.viewHeight = this.stage.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
      this.fit = Math.min(1, this.viewWidth / this.sourceWidth, this.viewHeight / this.sourceHeight);
      this.width = this.sourceWidth * this.fit;
      this.height = this.sourceHeight * this.fit;
      this.maxScale = Math.min(8, Math.max(2, 2 / this.fit));
      this.image.style.width = `${this.width}px`;
      this.image.style.height = `${this.height}px`;
      this.scale = Math.min(this.scale, this.maxScale);
      this.render();
    }

    point(event) {
      const rect = this.stage.getBoundingClientRect();
      return { x: event.clientX - rect.left - rect.width / 2, y: event.clientY - rect.top - rect.height / 2 };
    }

    zoom(next, point, animate = false) {
      next = clamp(next, 1, this.maxScale);
      const ratio = next / this.scale;
      this.x = point.x - (point.x - this.x) * ratio;
      this.y = point.y - (point.y - this.y) * ratio;
      this.scale = next;
      this.render(animate);
    }

    move(event) {
      const previous = this.pointers.get(event.pointerId);
      if (!previous) return;
      event.preventDefault();
      const before = [...this.pointers.values()];
      const point = this.point(event);
      this.pointers.set(event.pointerId, point);
      if (Math.hypot(point.x - previous.x, point.y - previous.y) > 2) this.moved = true;
      if (this.pointers.size === 2) {
        const after = [...this.pointers.values()];
        const distance = (pair) => Math.hypot(pair[1].x - pair[0].x, pair[1].y - pair[0].y);
        const center = (pair) => ({ x: (pair[0].x + pair[1].x) / 2, y: (pair[0].y + pair[1].y) / 2 });
        const oldCenter = center(before), newCenter = center(after);
        this.zoom(this.scale * distance(after) / Math.max(1, distance(before)), oldCenter);
        this.x += newCenter.x - oldCenter.x;
        this.y += newCenter.y - oldCenter.y;
      } else if (this.scale > 1) {
        this.x += point.x - previous.x;
        this.y += point.y - previous.y;
      }
      this.render();
    }

    render(animate = false) {
      const limitX = Math.max(0, (this.width * this.scale - this.viewWidth) / 2);
      const limitY = Math.max(0, (this.height * this.scale - this.viewHeight) / 2);
      this.x = clamp(this.x, -limitX, limitX);
      this.y = clamp(this.y, -limitY, limitY);
      const previous = this.image.style.transform;
      this.moveAnimation?.cancel();
      this.image.style.transform = `translate(-50%, -50%) translate3d(${this.x}px, ${this.y}px, 0) scale(${this.scale})`;
      this.resetButton.hidden = this.scale === 1;
      this.stage.classList.toggle('is-zoomed', this.scale > 1);
      if (animate && !reduced()) this.moveAnimation = this.image.animate([{ transform: previous }, { transform: this.image.style.transform }], { duration: 180, easing: 'ease-out' });
    }

    reset(animate = false) {
      this.scale = 1;
      this.x = this.y = 0;
      this.render(animate);
    }

    async hide(immediate = false) {
      if (!this.hasAttribute('open') || (this.closing && !immediate)) return;
      this.closing = true;
      ++this.request;
      this.animation?.cancel();
      this.moveAnimation?.cancel();
      this.pointers.clear();
      this.stage.classList.remove('is-dragging');
      if (immediate || reduced()) { this.finishClose(!immediate); return; }
      this.animation = this.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 180, easing: 'ease-in' });
      try { await this.animation.finished; } catch { return; }
      if (!this.closing) return;
      this.finishClose();
    }

    finishClose(restoreFocus = true) {
      this.removeAttribute('open');
      this.removeAttribute('aria-busy');
      this.image.removeAttribute('src');
      window.AcLayerManager?.release(this.id, { restoreFocus: this.isConnected && this.openedBy?.isConnected && restoreFocus });
      document.body.dispatchEvent(new CustomEvent('modalClosed'));
    }
  }
  customElements.define('product-modal', ProductImageViewer);

  const hoverQuery = matchMedia('(hover: hover) and (pointer: fine)');
  let hover, frame;
  const clearHover = (immediate = true) => {
    if (!hover) return;
    const image = hover.image;
    hover.opener.removeEventListener('pointermove', moveHover);
    hover = null;
    cancelAnimationFrame(frame);
    frame = 0;
    if (immediate || reduced()) image?.remove();
    else if (image) image.animate([{ opacity: 1 }, { opacity: 0 }], { duration: 150 }).finished.then(() => image.remove()).catch(() => image.remove());
  };
  const renderHover = () => {
    frame = 0;
    if (!hover?.image) return;
    const { image, width, height, baseWidth, baseHeight, x, y, zoom } = hover;
    const tx = (0.5 - x / width) * Math.max(0, baseWidth * zoom - width);
    const ty = (0.5 - y / height) * Math.max(0, baseHeight * zoom - height);
    image.style.transform = `translate(-50%, -50%) translate3d(${tx}px, ${ty}px, 0) scale(${zoom})`;
  };
  document.addEventListener('pointerover', async (event) => {
    const opener = event.target.closest('[data-ac-hover-zoom="true"]');
    if (!opener || !hoverQuery.matches || event.pointerType !== 'mouse' || event.buttons || opener.contains(event.relatedTarget)) return;
    clearHover();
    const rect = opener.getBoundingClientRect();
    const state = hover = { opener, width: rect.width, height: rect.height, x: event.clientX - rect.left, y: event.clientY - rect.top, rect };
    opener.addEventListener('pointermove', moveHover, { passive: true });
    try {
      const image = await fullImage(opener);
      if (hover !== state) return;
      const fit = getComputedStyle(opener.querySelector('img')).objectFit === 'cover'
        ? Math.max(state.width / image.naturalWidth, state.height / image.naturalHeight)
        : Math.min(state.width / image.naturalWidth, state.height / image.naturalHeight);
      state.baseWidth = image.naturalWidth * fit;
      state.baseHeight = image.naturalHeight * fit;
      state.zoom = Math.min(2.5, Math.max(1, 1 / fit));
      state.image = image;
      image.className = 'ac-ref-product__hover-image';
      image.alt = '';
      image.draggable = false;
      image.style.width = `${state.baseWidth}px`;
      image.style.height = `${state.baseHeight}px`;
      opener.append(image);
      renderHover();
      image.animate([{ opacity: 0 }, { opacity: 1 }], { duration: reduced() ? 0 : 180 });
    } catch { if (hover === state) clearHover(); }
  });
  const moveHover = (event) => {
    if (!hover) return;
    if (event.buttons) { clearHover(); return; }
    hover.x = clamp(event.clientX - hover.rect.left, 0, hover.width);
    hover.y = clamp(event.clientY - hover.rect.top, 0, hover.height);
    if (!frame) frame = requestAnimationFrame(renderHover);
  };
  document.addEventListener('pointerout', (event) => {
    if (hover?.opener.contains(event.target) && !hover.opener.contains(event.relatedTarget)) clearHover(false);
  });
  document.addEventListener('pointerdown', () => clearHover(), { passive: true });
  document.addEventListener('scroll', () => clearHover(), { passive: true, capture: true });
  document.addEventListener('visibilitychange', () => { if (document.hidden) clearHover(); });
  hoverQuery.addEventListener('change', () => clearHover());
  document.addEventListener('shopify:section:unload', (event) => {
    clearHover();
    const id = event.detail?.sectionId || event.target.id?.replace('shopify-section-', '');
    for (const modal of document.querySelectorAll('product-modal')) if (modal.dataset.section === id) modal.remove();
  });
  window.addEventListener('pagehide', () => {
    clearHover();
    for (const modal of document.querySelectorAll('product-modal[open]')) modal.hide(true);
  });
})();
